import { useState } from "react";

import RelativeLink from "common/components/RelativeLink";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import ProfileImage from "common/components/ui/ProfileImage";
import RadioButton from "common/components/ui/RadioButton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Lead } from "common/types";

export default function MessageLeadDialog({
  lead,
  isOpen,
  onClose,
}: {
  lead: Lead;
  isOpen: boolean;
  onClose: () => void;
}) {
  const isTabletOrDesktop = useTwBreakpoint("lg");
  const { accounts } = useSelectedWorkspaceContext();
  const [selectedAccountId, setSelectedAccountId] = useState<string>(null);

  const Component = isTabletOrDesktop ? Dialog : Drawer;
  const Content = isTabletOrDesktop ? DialogContent : DrawerContent;
  const Close = isTabletOrDesktop ? DialogClose : DrawerClose;
  const Footer = isTabletOrDesktop ? DialogFooter : DrawerFooter;
  const Body = isTabletOrDesktop ? DialogBody : DrawerBody;
  const Header = isTabletOrDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Content className="md:max-h-[80%]">
        <Header
          title="Choose sender"
          subtitle="Choose from which account you want to message this lead"
        />

        <Body className="gap-y-2.5 lg:gap-y-4">
          {lead.lead_of.map((leadOfId) => {
            const {
              id,
              picture_url: pictureUrl,
              full_name: fullName,
              occupation,
            } = accounts.find((account) => account.id === leadOfId);

            return (
              <button
                key={id}
                className="flex items-center gap-x-2 rounded-3xl border border-black-200 p-3"
                type="button"
                onClick={() => setSelectedAccountId(id)}
              >
                <ProfileImage src={pictureUrl} size="lg" />

                <div className="flex flex-col gap-y-1 text-left">
                  <span className="text-md-bold">{fullName}</span>
                  <span className="line-clamp-1 text-xs text-black-500">
                    {occupation}
                  </span>
                </div>

                <RadioButton
                  checked={selectedAccountId === id}
                  isStyleOnly
                  className="ml-auto"
                />
              </button>
            );
          })}
        </Body>

        <Footer className="justify-end">
          <Close asChild>
            <Button variant="secondary-black">Cancel</Button>
          </Close>

          <Button disabled={!selectedAccountId} asChild>
            <RelativeLink
              to="inbox"
              state={{ lead, accountId: selectedAccountId }}
            >
              Go to chat
            </RelativeLink>
          </Button>
        </Footer>
      </Content>
    </Component>
  );
}
